import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import toast from 'react-hot-toast';
import { getCategoryTranslator } from '../utils/categoryTranslation';
import { nativeFetch } from '../utils/nativeApi';

const ANALYTICS_FILTER_KEY = 'shkalot_analytics_excluded_categories';

// Category colors that match the neobrutalist design
const CATEGORY_COLORS = [
  '#FF6B6B', // coral red
  '#4ECDC4', // teal
  '#FFE66D', // yellow
  '#95E1D3', // mint
  '#F38181', // salmon
  '#AA96DA', // lavender
  '#FCBAD3', // pink
  '#A8D8EA', // sky blue
  '#FF9F43', // orange
  '#6BCB77', // green
  '#C4E538', // lime
  '#FDA7DF', // magenta
];

function AnalyticsPage() {
  const { t } = useTranslation(['analytics', 'common', 'categories']);
  const translateCategory = getCategoryTranslator(t);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('this_month');
  const [excludedCategories, setExcludedCategories] = useState(() => {
    const saved = localStorage.getItem(ANALYTICS_FILTER_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Save excluded categories to localStorage
  useEffect(() => {
    localStorage.setItem(ANALYTICS_FILTER_KEY, JSON.stringify(excludedCategories));
  }, [excludedCategories]);

  // Filtered analytics data
  const filteredAnalytics = useMemo(() => {
    if (!analytics) return null;
    
    const includedCategories = analytics.categories?.filter(
      cat => !excludedCategories.includes(cat.name)
    ) || [];
    
    const filteredTotal = includedCategories.reduce((sum, cat) => sum + cat.total, 0);
    // Use scaled_budget for percentage calculation
    const budget = analytics.scaled_budget || analytics.monthly_budget;
    const filteredPercentage = budget && budget > 0
      ? (filteredTotal / budget) * 100
      : 0;
    
    return {
      ...analytics,
      filteredCategories: includedCategories,
      filteredTotal,
      filteredPercentage,
    };
  }, [analytics, excludedCategories]);

  useEffect(() => {
    if (period !== 'custom') {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAnalytics = async (startTs = null, endTs = null) => {
    try {
      setLoading(true);
      let url = `/api/analytics?period=${period}`;
      if (period === 'custom' && startTs && endTs) {
        url = `/api/analytics?period=custom&start_date=${startTs}&end_date=${endTs}`;
      }
      const response = await nativeFetch(url);

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (!customStartDate || !customEndDate) {
      toast.error(t('selectBothDates', 'Please select both start and end dates'));
      return;
    }
    const startTs = Math.floor(new Date(customStartDate).getTime() / 1000);
    const endTs = Math.floor(new Date(customEndDate).getTime() / 1000);
    if (startTs > endTs) {
      toast.error(t('invalidDateRange', 'Start date must be before end date'));
      return;
    }
    setShowDatePicker(false);
    fetchAnalytics(startTs, endTs);
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GEL': '₾',
      'ILS': '₪',
      'JPY': '¥'
    };
    return symbols[currency] || currency;
  };

  const formatAmount = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPeriodLabel = (periodKey) => {
    if (periodKey === 'custom' && analytics?.start_date && analytics?.end_date) {
      const start = new Date(analytics.start_date * 1000);
      const end = new Date(analytics.end_date * 1000);
      const formatDate = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    const labels = {
      'this_month': t('common:time.thisMonth'),
      'last_month': t('common:time.lastMonth'),
      'last_3_months': t('last3Months', 'Last 3 Months'),
      'custom': t('customRange', 'Custom Range')
    };
    return labels[periodKey] || periodKey;
  };

  const toggleCategoryExclusion = (categoryName) => {
    setExcludedCategories(prev => 
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const clearAllFilters = () => {
    setExcludedCategories([]);
  };

  // Calculate donut chart segments
  const getDonutSegments = () => {
    if (!filteredAnalytics?.filteredCategories?.length) return [];
    
    const total = filteredAnalytics.filteredTotal;
    if (total === 0) return [];
    
    let currentOffset = 0;
    const circumference = 2 * Math.PI * 88; // r=88
    const minSegmentLength = 8; // Minimum visible segment size in pixels
    
    return filteredAnalytics.filteredCategories.map((cat, index) => {
      const percentage = cat.total / total;
      // Ensure minimum segment size for visibility, but cap at actual percentage
      const rawSegmentLength = percentage * circumference;
      const segmentLength = Math.max(rawSegmentLength, minSegmentLength);
      const segment = {
        color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
        dashArray: `${segmentLength} ${circumference}`,
        dashOffset: -currentOffset,
        name: cat.name,
        icon: cat.icon,
        total: cat.total,
        percentage: percentage * 100,
      };
      currentOffset += rawSegmentLength; // Use raw length for offset calculation
      return segment;
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-2xl font-bold">{t('common:messages.loading')}</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!analytics) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-xl font-bold text-gray-600">{t('noData')}</div>
        </div>
      </DashboardLayout>
    );
  }

  const scaledBudget = analytics.scaled_budget || analytics.monthly_budget;
  const hasBudget = scaledBudget && scaledBudget > 0;
  const donutSegments = getDonutSegments();
  const displayTotal = filteredAnalytics?.filteredTotal ?? analytics.total_spent;
  const displayPercentage = filteredAnalytics?.filteredPercentage ?? analytics.budget_percentage ?? 0;

  return (
    <DashboardLayout>
      <div className="p-4 space-y-6 pb-24">
        {/* Header with Filter Button */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-black">📊 {t('title')}</h1>
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="relative px-3 py-2 border-2 border-black rounded-lg font-bold bg-white shadow-hard-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            🔍 {t('filter', 'Filter')}
            {excludedCategories.length > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary border-2 border-black rounded-full text-xs font-bold flex items-center justify-center">
                {excludedCategories.length}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilterPanel && analytics.categories && (
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{t('excludeCategories', 'Exclude Categories')}</h3>
              {excludedCategories.length > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm font-bold text-red-500 hover:underline"
                >
                  {t('clearAll', 'Clear all')}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {analytics.categories.map((cat) => {
                const isExcluded = excludedCategories.includes(cat.name);
                return (
                  <button
                    key={cat.name}
                    onClick={() => toggleCategoryExclusion(cat.name)}
                    className={`px-3 py-1 border-2 border-black rounded-lg text-sm font-bold transition-colors ${
                      isExcluded
                        ? 'bg-gray-300 text-gray-500 line-through'
                        : 'bg-white hover:bg-gray-100'
                    }`}
                  >
                    {cat.icon} {translateCategory(cat.name)}
                  </button>
                );
              })}
            </div>
            {excludedCategories.length > 0 && (
              <p className="text-sm text-gray-600 mt-3">
                {t('excludedNote', 'Excluded categories are not counted in the total and chart.')}
              </p>
            )}
          </div>
        )}

        {/* Period Selector */}
        <div className="flex gap-3 overflow-x-auto pb-2">
          {['this_month', 'last_month', 'last_3_months'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 font-bold transition-all ${
                period === p
                  ? 'bg-accent text-white border-2 border-black shadow-hard-sm'
                  : 'bg-white text-black border-2 border-black hover:shadow-hard-sm'
              }`}
            >
              <span className="text-lg">📅</span>
              {getPeriodLabel(p)}
            </button>
          ))}
          <button
            onClick={() => {
              setPeriod('custom');
              setShowDatePicker(true);
            }}
            className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 font-bold transition-all ${
              period === 'custom'
                ? 'bg-accent text-white border-2 border-black shadow-hard-sm'
                : 'bg-white text-black border-2 border-black hover:shadow-hard-sm'
            }`}
          >
            <span className="text-lg">📆</span>
            {period === 'custom' && analytics?.start_date ? getPeriodLabel('custom') : t('customRange', 'Custom')}
          </button>
        </div>

        {/* Custom Date Picker Modal */}
        {showDatePicker && (
          <div className="card">
            <h3 className="font-bold mb-3">{t('selectDateRange', 'Select Date Range')}</h3>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-bold mb-1">{t('startDate', 'Start Date')}</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-black rounded-lg font-medium"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-bold mb-1">{t('endDate', 'End Date')}</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-black rounded-lg font-medium"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCustomDateSubmit}
                className="flex-1 px-4 py-2 bg-accent text-white border-2 border-black rounded-lg font-bold shadow-hard-sm hover:shadow-hard transition-all"
              >
                {t('apply', 'Apply')}
              </button>
              <button
                onClick={() => {
                  setShowDatePicker(false);
                  if (!analytics?.start_date || period !== 'custom') {
                    setPeriod('this_month');
                  }
                }}
                className="px-4 py-2 bg-white border-2 border-black rounded-lg font-bold hover:bg-gray-100 transition-colors"
              >
                {t('cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Budget Progress Card */}
        <div className="card">
          <h2 className="text-lg font-bold text-gray-600 mb-4 text-center">
            {t('totalSpending')} ({getPeriodLabel(period)})
            {excludedCategories.length > 0 && (
              <span className="text-xs ml-2 text-gray-400">({t('filtered', 'filtered')})</span>
            )}
          </h2>

          {/* Circular Progress Ring - Multi-color by category */}
          <div className="flex justify-center mb-6">
            <div className="relative w-48 h-48">
              <svg 
                className="w-full h-full transform -rotate-90"
                onClick={() => setSelectedSegment(null)}
              >
                {/* Background circle */}
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="#e5e7eb"
                  strokeWidth="16"
                  fill="none"
                />
                {/* Category segments */}
                {donutSegments.length > 0 ? (
                  donutSegments.map((segment, index) => (
                    <circle
                      key={segment.name}
                      cx="96"
                      cy="96"
                      r="88"
                      stroke={segment.color}
                      strokeWidth="20"
                      fill="none"
                      strokeDasharray={segment.dashArray}
                      strokeDashoffset={segment.dashOffset}
                      className="cursor-pointer hover:opacity-80"
                      style={{ transition: 'stroke-dasharray 0.3s, stroke-dashoffset 0.3s, opacity 0.2s' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSegment(selectedSegment?.name === segment.name ? null : segment);
                      }}
                    />
                  ))
                ) : hasBudget ? (
                  /* Fallback single color if no categories */
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke={displayPercentage > 100 ? '#ef4444' : displayPercentage > 75 ? '#f59e0b' : '#10b981'}
                    strokeWidth="16"
                    fill="none"
                    strokeDasharray={`${Math.min(displayPercentage, 100) * 5.53} 553`}
                    strokeLinecap="round"
                  />
                ) : null}
              </svg>
              
              {/* Center content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-3xl font-black">
                  {formatAmount(displayTotal, analytics.currency)}
                </p>
                {hasBudget && (
                  <p className="text-sm text-gray-600 mt-1">
                    of {formatAmount(scaledBudget, analytics.currency)}
                  </p>
                )}
              </div>

              {/* Segment tooltip popup */}
              {selectedSegment && (
                <div 
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-2 border-black rounded-lg px-3 py-2 shadow-hard-sm z-10 text-center min-w-[120px]"
                  onClick={() => setSelectedSegment(null)}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span 
                      className="w-3 h-3 rounded-full border border-black"
                      style={{ backgroundColor: selectedSegment.color }}
                    />
                    <span className="font-bold text-sm">{selectedSegment.icon} {translateCategory(selectedSegment.name)}</span>
                  </div>
                  <p className="text-lg font-black">{formatAmount(selectedSegment.total, analytics.currency)}</p>
                  <p className="text-xs text-gray-600">{selectedSegment.percentage.toFixed(1)}%</p>
                </div>
              )}
            </div>
          </div>

          {/* Budget percentage */}
          {hasBudget ? (
            <p className="text-xl font-bold text-center">
              {displayPercentage.toFixed(1)}% {t('budgetUsed', 'of Budget Used')}
            </p>
          ) : (
            <p className="text-center text-gray-600">
              <span className="font-bold">{t('noBudget', 'No budget set.')}</span> {t('setBudgetHint', 'Set a monthly budget in Settings to track progress.')}
            </p>
          )}
        </div>

        {/* Category Breakdown */}
        {analytics.categories && analytics.categories.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-black mb-4">{t('spendingByCategory')}</h3>
            <div className="space-y-4">
              {analytics.categories.map((category, index) => {
                const isExcluded = excludedCategories.includes(category.name);
                const categoryPercentage = hasBudget 
                  ? (category.total / scaledBudget) * 100 
                  : (category.total / analytics.total_spent) * 100;
                const categoryColor = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
                
                return (
                  <div 
                    key={index} 
                    className={`space-y-2 ${isExcluded ? 'opacity-40' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full border border-black flex-shrink-0"
                          style={{ backgroundColor: categoryColor }}
                        />
                        <span className="text-2xl">{category.icon}</span>
                        <span className={`font-bold ${isExcluded ? 'line-through' : ''}`}>
                          {translateCategory(category.name)}
                        </span>
                        <span className="text-sm text-gray-600">({category.count})</span>
                      </div>
                      <span className={`font-bold ${isExcluded ? 'line-through' : ''}`}>
                        {formatAmount(category.total, analytics.currency)}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full h-3 bg-gray-200 rounded-full border-2 border-black overflow-hidden">
                      <div
                        className="h-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(categoryPercentage, 100)}%`,
                          backgroundColor: isExcluded ? '#9ca3af' : categoryColor
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No expenses message */}
        {analytics.expense_count === 0 && (
          <div className="card text-center">
            <span className="text-6xl mb-4 block">📭</span>
            <p className="text-xl font-bold mb-2">{t('noExpenses', 'No Expenses Yet')}</p>
            <p className="text-gray-600">
              {t('startRecording', 'Start recording expenses to see your analytics!')}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AnalyticsPage;
